import axios from 'axios';
import { API_BASE_URL } from '../api';
import cacheService from '../utils/cacheService';

export const getProducts = async (page = 1, limit = 20, search = '', filters = {}) => {
  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
  const params = { page, limit };
  if (search) {
    params.search = search;
  }
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params[key] = value;
    }
  });
  const res = await axios.get(`${API_BASE_URL}/products`, { 
    headers,
    params,
    timeout: 30000 // 30 seconds timeout for getting products
  });
  return res.data.data;
};

export const semanticSearchProducts = async ({
  queryText,
  page = 1,
  limit = 20,
  filters = {},
  candidateLimit = 250,
  maxNewEmbeddings = 25
}) => {
  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
  const res = await axios.post(
    `${API_BASE_URL}/products/search/semantic`,
    { queryText, page, limit, filters, candidateLimit, maxNewEmbeddings },
    { headers, timeout: 60000 }
  );
  return res.data.data;
};

export const visualSearchProducts = async ({
  imageUrl,
  page = 1,
  limit = 20,
  filters = {},
  candidateLimit = 250,
  maxNewEmbeddings = 25,
  strictMode = true,
  minScore
}) => {
  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
  const res = await axios.post(
    `${API_BASE_URL}/products/search/visual`,
    { imageUrl, page, limit, filters, candidateLimit, maxNewEmbeddings, strictMode, minScore },
    { headers, timeout: 90000 }
  );
  // Support both `{ success, data: { products, scores } }` and a flat payload.
  return res.data?.data ?? res.data;
};

export const reindexProductEmbeddings = async ({
  userId,
  limit = 200,
  onlyMissing = true
} = {}) => {
  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
  const res = await axios.post(
    `${API_BASE_URL}/products/search/reindex`,
    { userId, limit, onlyMissing },
    { headers, timeout: 120000 }
  );
  return res.data.data;
};

export const getFabricTypes = async () => {
  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
  const res = await axios.get(`${API_BASE_URL}/products/fabric-types`, {
    headers,
    timeout: 15000
  });
  return res.data?.data?.fabricTypes || [];
};

export const getWeaveKnitValues = async () => {
  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
  const res = await axios.get(`${API_BASE_URL}/products/weave-knit`, {
    headers,
    timeout: 15000
  });
  return res.data?.data?.weaveKnit || [];
};

export const getOpacityValues = async () => {
  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
  const res = await axios.get(`${API_BASE_URL}/products/opacity`, {
    headers,
    timeout: 15000
  });
  return res.data?.data?.opacity || [];
};

export const getStretchValues = async () => {
  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
  const res = await axios.get(`${API_BASE_URL}/products/stretch`, {
    headers,
    timeout: 15000
  });
  return res.data?.data?.stretch || [];
};

export const getColorValues = async () => {
  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
  const res = await axios.get(`${API_BASE_URL}/products/colors`, {
    headers,
    timeout: 15000
  });
  return res.data?.data?.colors || [];
};

export const getProductById = async (id) => {
  if (!id) {
    throw new Error('Product ID is required');
  }

  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };

  const res = await axios.get(`${API_BASE_URL}/products/${id}`, { 
    headers,
    timeout: 30000
  });

  return res.data?.data?.product || res.data?.data || null;
};

export const createProduct = async (product) => {
  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
  
  console.log('🔍 API - createProduct payload:', product);
  console.log('🔍 API - createProduct fabricTexture:', product.fabricTexture);
  console.log('🔍 API - createProduct colorVariants:', product.colorVariants);
  
  const res = await axios.post(`${API_BASE_URL}/products`, product, { 
    headers,
    timeout: 60000 // 1 minute timeout for creating products (includes image upload)
  });
  return res.data.data.product;
};

export const updateProduct = async (id, product) => {
  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
  
  console.log('🔍 API - updateProduct payload:', product);
  console.log('🔍 API - updateProduct fabricTexture:', product.fabricTexture);
  console.log('🔍 API - updateProduct colorVariants:', product.colorVariants);
  
  const res = await axios.put(`${API_BASE_URL}/products/${id}`, product, { 
    headers,
    timeout: 60000 // 1 minute timeout for updating products (includes image upload)
  });
  return res.data.data.product;
};

export const extractColorVariants = async (imageUrl, maxColors = 100) => {
  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
  const res = await axios.post(`${API_BASE_URL}/color/getcolorvariants`, { imageUrl, maxColors }, { headers, timeout: 60000 });
  return res.data;
};

export const deleteProduct = async (id) => {
  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
  await axios.delete(`${API_BASE_URL}/products/${id}`, { 
    headers,
    timeout: 30000 // 30 seconds timeout for deleting products
  });
};

export const updateProductVisibility = async (id, isPublic) => {
  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
  const res = await axios.patch(`${API_BASE_URL}/products/${id}/visibility`, { isPublic }, {
    headers,
    timeout: 15000
  });
  // Fabric collection GET responses cache embedded fabrics; invalidate so refresh shows correct isPublic
  cacheService.deleteMatchingLogicalPrefix('collection_');
  cacheService.deleteMatchingLogicalPrefix('collections_');
  return res.data.data.product;
};

// Get public products by phone number (no auth required)
export const getPublicProductsByPhone = async (phoneNumber, page = 1, limit = 20, search = '') => {
  const headers = {
    'Content-Type': 'application/json'
  };
  const params = { page, limit };
  if (search) {
    params.search = search;
  }
  const slug = encodeURIComponent(String(phoneNumber || '').trim().toLowerCase());
  const res = await axios.get(`${API_BASE_URL}/products/public/${slug}`, { 
    headers,
    params,
    timeout: 30000 // 30 seconds timeout
  });
  return res.data.data;
};

// Get a single fabric by ID (for notification deep links)
// Passes token when available so subscribers can view non-public fabrics
export const getPublicFabricById = async (id) => {
  if (!id) throw new Error('Fabric ID is required');
  const token = localStorage.getItem('token');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const res = await axios.get(`${API_BASE_URL}/products/public/fabric/${id}`, {
    headers,
    timeout: 30000
  });
  return res.data?.data?.product || null;
};

// Public semantic search - no auth required
export const semanticSearchPublicFabrics = async ({
  queryText,
  page = 1,
  limit = 20,
  filters = {},
  candidateLimit = 250,
  maxNewEmbeddings = 10
}) => {
  const headers = { 'Content-Type': 'application/json' };
  const token = localStorage.getItem('token');
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  const res = await axios.post(
    `${API_BASE_URL}/products/public/search/semantic`,
    { queryText, page, limit, filters, candidateLimit, maxNewEmbeddings },
    { headers, timeout: 60000 }
  );
  return res.data.data;
};

// Public visual/image search - no auth required
export const visualSearchPublicFabrics = async ({
  imageUrl,
  page = 1,
  limit = 20,
  filters = {},
  candidateLimit = 250,
  maxNewEmbeddings = 10,
  strictMode = true
}) => {
  const headers = { 'Content-Type': 'application/json' };
  const token = localStorage.getItem('token');
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  const res = await axios.post(
    `${API_BASE_URL}/products/public/search/visual`,
    { imageUrl, page, limit, filters, candidateLimit, maxNewEmbeddings, strictMode },
    { headers, timeout: 90000 }
  );
  return res.data?.data ?? res.data;
};

// Get all public fabrics (no auth required)
export const getAllPublicFabrics = async (page = 1, limit = 20, search = '', filters = {}) => {
  const headers = {
    'Content-Type': 'application/json'
  };
  const params = { page, limit };
  if (search) {
    params.search = search;
  }
  Object.entries(filters || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params[key] = value;
    }
  });
  const res = await axios.get(`${API_BASE_URL}/products/public/all`, { 
    headers,
    params,
    timeout: 30000 // 30 seconds timeout
  });
  return res.data.data;
}; 

// Get filter options for public fabrics marketplace
export const getPublicFabricFilterOptions = async () => {
  const headers = {
    'Content-Type': 'application/json'
  };
  const res = await axios.get(`${API_BASE_URL}/products/public/filter-options`, {
    headers,
    timeout: 20000
  });
  return res.data?.data || {
    fabricTypes: [],
    weaveKnit: [],
    opacity: [],
    stretch: [],
    colors: []
  };
};

/** Recommended fabrics: fabric-type filter, attribute sort, garment image visual search. */
export const fetchRecommendedFabricsForGarment = async ({
  inspirationalDesignId,
  suitableFabricAttributes,
  garmentImageUrl,
} = {}) => {
  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
  const res = await axios.post(
    `${API_BASE_URL}/products/recommended-for-garment`,
    { inspirationalDesignId, suitableFabricAttributes, garmentImageUrl },
    { headers, timeout: 35000 }
  );
  return res.data?.data || { fabrics: [], matchTokensUsed: 0 };
};